

import { DandDFeatureSource } from "@dndModels/feature_sources/SystemFeatureSource";

import { 
  CharacterLimitation, 
  Limitations, 
  Recipe, 
  SpellLimitation, 
  UpgradableNumber, 
  WeaponLimitation
} from "@dndModels";
import { EXTRA_BASE_TYPES, RARITY_NUM_TO_STR_MAP, REFRESH_RULES } from "@dndModels/Constants";
import { 
  DamageType, 
  DataType, 
  ExtraBaseType, 
  ItemType, 
  RefreshRuleType,
  SpellLevel,
  WorldSetting
} from "@dndModels/Types";

export class MagicItemKeyword extends DandDFeatureSource {
  data_type: DataType = "magic_item_keyword";
  feature_source_type = "MagicItemKeyword";
  uses_specific = false;
  base_item_types: ItemType[] = [];

  get item_types_string() {
    return this.base_item_types.join(", ");
  }

  get item_type() {
    return this.base_item_types[0] || "Other";
  }

  base_item_details: string[] = [];
  base_item_ids: string[] = [];
  exclusions = "";
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  
  // rarity: number = 0; // Common (0), Uncommon (1), Rare (2), Very-Rare (3), Legendary (4), Artifact (5), Special (-1)
  min_rarity = 0;
  max_rarity = 0;
  name_formula = "{base_item.name}"; // example: {base_item.name} of {feature_bases[0].feature_choices[0].selected_option.name} Resistance
  rarity_names: { [rarity: number]: string } = {};

  get rarities() {
    const rarities: string[] = [];
    for (let i = this.min_rarity; i <= this.max_rarity; ++i) {
      const rarity = RARITY_NUM_TO_STR_MAP[i];
      if (rarity) {
        rarities.push(rarity);
      }
    }
    return rarities;
  }

  get rarities_string() {
    return this.rarities.join(", ");
  }

  recipes: Recipe[] = [];

  attunement = false;
  limitations: CharacterLimitation[] = [];
  charges = 0;
  charge_refresh_rule: RefreshRuleType = "None";
  refresh_dice_count = 0;
  refresh_dice_size = 0;
  refresh_dice_flat = 0;
  invert_charges = false;
  // bonus_damages: RollPlus[] = [];

  // Extra Base Type is for creating things that draw on something else to 
  // define the specifics of it.  It works similarly to Selectable, 
  // but instead of the user getting to select as part of the feature, it 
  // just creates different items for each option.
  // For Example, spell scrolls would have an extra_base_type === "Spell"
  // Potions of Resistance would have an extra_base_type === "Damage Type"
  extra_base_type: ExtraBaseType = "None";
  // We probably want to limit these, 
  // if for no other reason than to give different rarities
  // For Spell this is Levels, on Spell Lists, and Schools.
  // For the others it's a list of options.
  extra_base_limits: any = null; 
  extra_base_limit: Limitations = new Limitations();

  spell_dc: UpgradableNumber = new UpgradableNumber();
  spell_attack: UpgradableNumber = new UpgradableNumber();
  spell_mod: UpgradableNumber = new UpgradableNumber();
  
  // level: number = 0;
  environments: string[] = ["Any"];
  // settings: string[] = ["Any"];
  possible_base_item_ids: string[] = [];
  proficiency_req_options: string[] = []; // Empty means use the standard, ["None"] means that it removes all requirements, otherwise it's a list of options
  weapon_mastery_override = ""; // If it's "Known" it means that the char can use the weapon mastery even if they wouldn't otherwise have it.  Otherwise it can also be an _id of a weapon_mastery.
  ignore_stealth_disadvantage = false;
  override_weapon_damage_type: DamageType | "" = "";
  // This allows selecting specific base weapons, 
  // and if the char is proficient in any of them, 
  // then they're proficient in a weapon that has this MIK.
  // Adding it for the Sun Blade (and Sunsword which is based on it), 
  // which is a Longsword, but says you're proficient with it if you're proficient with Longswords or Shortswords.
  proficiency_as_weapons: string[] = []; 

  parse_errors: string[] = [];

  constructor(obj?: any) {
    super(obj, "MagicItemKeyword");
    if (obj) {
      this.uses_specific = obj.uses_specific ? obj.uses_specific : false;
      this.base_item_types = [];
      if (obj.base_item_types) {
        this.base_item_types = obj.base_item_types;
      } else if (obj.base_item_type) {
        this.base_item_types = [obj.base_item_type];
      }
      this.base_item_details = obj.base_item_details ? obj.base_item_details : [];
      this.base_item_ids = obj.base_item_ids ? obj.base_item_ids : (obj.base_item_id ? [obj.base_item_id] : []);
      this.exclusions = obj.exclusions ? `${obj.exclusions}` : "";
      
      this.min_rarity = obj.min_rarity !== undefined ? obj.min_rarity : (obj.rarity ? obj.rarity : 0);
      this.max_rarity = obj.max_rarity !== undefined ? obj.max_rarity : this.min_rarity;
      this.rarity_names = obj.rarity_names ? obj.rarity_names : {};
      this.name_formula = obj.name_formula ? `${obj.name_formula}` : "{base_item.name}";

      this.recipes = obj.recipes ? obj.recipes.map((o: any) => new Recipe(o)) : (obj.recipe ? [new Recipe(obj.recipe)] : []);

      this.attunement = obj.attunement ? obj.attunement : false;
      this.limitations = [];
      if (obj.limitations) {
        obj.limitations.forEach((l: any) => {
          this.limitations.push(new CharacterLimitation(l));
        });
      }
      this.charges = obj.charges ? obj.charges : 0;
      this.charge_refresh_rule = obj.charge_refresh_rule ? obj.charge_refresh_rule : "None";
      if (!REFRESH_RULES.includes(this.charge_refresh_rule)) {
        this.charge_refresh_rule = "None";
      }
      this.refresh_dice_count = obj.refresh_dice_count ? obj.refresh_dice_count : 0;
      this.refresh_dice_size = obj.refresh_dice_size ? obj.refresh_dice_size : 0;
      this.refresh_dice_flat = obj.refresh_dice_flat ? obj.refresh_dice_flat : 0;
      this.invert_charges = obj.invert_charges ? obj.invert_charges : false;
      
      this.extra_base_type = obj.extra_base_type ? obj.extra_base_type : "None";
      if (!EXTRA_BASE_TYPES.includes(this.extra_base_type)) {
        this.extra_base_type = "None";
        console.log(obj.extra_base_type);
      }
      this.extra_base_limits = obj.extra_base_limits ? obj.extra_base_limits : {};
      if (this.extra_base_limits.length && this.extra_base_limits.length === 1 && this.extra_base_limits[0] === "Any") {
        this.extra_base_limits = [];
      }
      if (obj.extra_base_limit) {
        this.extra_base_limit = new Limitations(obj.extra_base_limit);
      } else {
        if (this.extra_base_type === "Spell") {
          // Old format of this.extra_base_limits for Spell { levels: string[], schools: string[], onSpellList: string[] }
          this.extra_base_limit.limitation_type = "Spell";
          const limit = new SpellLimitation();
          if (this.extra_base_limits.levels) {
            limit.levels = this.extra_base_limits.levels.map((o: string) => {
              if (o === "Cantrip") {
                return 0;
              } else {
                return +o;
              }
            }) as SpellLevel[];
          }
          if (this.extra_base_limits.schools) {
            limit.schools = this.extra_base_limits.schools;
          }
          if (this.extra_base_limits.onSpellList) {
            limit.list_ids = this.extra_base_limits.onSpellList;
          }
          this.extra_base_limit.limits.push(limit);
        }
      }
      this.spell_dc = obj.spell_dc ? new UpgradableNumber(obj.spell_dc) : new UpgradableNumber();
      this.spell_attack = obj.spell_attack ? new UpgradableNumber(obj.spell_attack) : new UpgradableNumber();
      this.spell_mod = obj.spell_mod ? new UpgradableNumber(obj.spell_mod) : new UpgradableNumber();
      // this.level = obj.level ? obj.level : 0;
      this.environments = obj.environments ? [...obj.environments] : ["Any"];
      // this.settings = obj.settings ? [...obj.settings] : ["Any"];
      if (obj.settings && obj.settings.length > 0 && this.world_settings.length === 0) {
        this.world_settings = obj.settings as WorldSetting[];
      }
      if (this.world_settings.includes("Any")) {
        this.world_settings = this.world_settings.filter(o => o !== "Any");
      }

      this.proficiency_req_options = obj.proficiency_req_options ? obj.proficiency_req_options : [];
      if (this.proficiency_req_options.includes("True None")) {
        this.proficiency_req_options = this.proficiency_req_options.filter(o => o !== "True None");
        this.proficiency_req_options.push("None");
      }
      this.weapon_mastery_override = obj.weapon_mastery_override ? obj.weapon_mastery_override : "";
      this.ignore_stealth_disadvantage = obj.ignore_stealth_disadvantage ? obj.ignore_stealth_disadvantage : false;
      this.override_weapon_damage_type = obj.override_weapon_damage_type ? obj.override_weapon_damage_type : "";
      this.proficiency_as_weapons = obj.proficiency_as_weapons ? obj.proficiency_as_weapons : [];

      if (this.name.includes(" Of ")) {
        this.name = this.name.replace(" Of ", " of ");
      }
      if (this.name_formula.includes("{obj.base_name}")) {
        this.name_formula = this.name_formula.replace("{obj.base_name}","{base_item.name}");
      }
      this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
      if (this.base_item_types.includes("Weapon") && !obj.weapon_limit) {
        this.weapon_limit.weapon_keyword_ids = this.base_item_details;
        this.base_item_details = [];
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.uses_specific = obj.uses_specific ? obj.uses_specific : false;
  //     this.base_item_types = [];
  //     if (obj.base_item_types) {
  //       this.base_item_types = obj.base_item_types;
  //     } else if (obj.base_item_type) {
  //       this.base_item_types = [obj.base_item_type];
  //     }
  //     this.base_item_details = obj.base_item_details ? obj.base_item_details : [];
  //     this.base_item_ids = obj.base_item_ids ? obj.base_item_ids : (obj.base_item_id ? [obj.base_item_id] : []);
  //     this.exclusions = obj.exclusions ? `${obj.exclusions}` : "";
      
  //     this.min_rarity = obj.min_rarity !== undefined ? obj.min_rarity : (obj.rarity ? obj.rarity : 0);
  //     this.max_rarity = obj.max_rarity !== undefined ? obj.max_rarity : this.min_rarity;
  //     this.rarity_names = obj.rarity_names ? obj.rarity_names : {};
  //     this.name_formula = obj.name_formula ? `${obj.name_formula}` : "{base_item.name}";

  //     this.recipes = obj.recipes ? obj.recipes.map((o: any) => new Recipe(o)) : (obj.recipe ? [new Recipe(obj.recipe)] : []);

  //     this.attunement = obj.attunement ? obj.attunement : false;
  //     this.limitations = [];
  //     if (obj.limitations) {
  //       obj.limitations.forEach((l: any) => {
  //         this.limitations.push(new CharacterLimitation(l));
  //       });
  //     }
  //     this.charges = obj.charges ? obj.charges : 0;
  //     this.charge_refresh_rule = obj.charge_refresh_rule ? obj.charge_refresh_rule : "None";
  //     if (!REFRESH_RULES.includes(this.charge_refresh_rule)) {
  //       this.charge_refresh_rule = "None";
  //     }
  //     this.refresh_dice_count = obj.refresh_dice_count ? obj.refresh_dice_count : 0;
  //     this.refresh_dice_size = obj.refresh_dice_size ? obj.refresh_dice_size : 0;
  //     this.refresh_dice_flat = obj.refresh_dice_flat ? obj.refresh_dice_flat : 0;
  //     this.invert_charges = obj.invert_charges ? obj.invert_charges : false;
      
  //     this.extra_base_type = obj.extra_base_type ? obj.extra_base_type : "None";
  //     if (!EXTRA_BASE_TYPES.includes(this.extra_base_type)) {
  //       this.extra_base_type = "None";
  //       console.log(obj.extra_base_type);
  //     }
  //     this.extra_base_limits = obj.extra_base_limits ? obj.extra_base_limits : {};
  //     if (this.extra_base_limits.length && this.extra_base_limits.length === 1 && this.extra_base_limits[0] === "Any") {
  //       this.extra_base_limits = [];
  //     }
  //     if (obj.extra_base_limit) {
  //       this.extra_base_limit = new Limitations(obj.extra_base_limit);
  //     } else {
  //       if (this.extra_base_type === "Spell") {
  //         // Old format of this.extra_base_limits for Spell { levels: string[], schools: string[], onSpellList: string[] }
  //         this.extra_base_limit.limitation_type = "Spell";
  //         const limit = new SpellLimitation();
  //         if (this.extra_base_limits.levels) {
  //           limit.levels = this.extra_base_limits.levels.map((o: string) => {
  //             if (o === "Cantrip") {
  //               return 0;
  //             } else {
  //               return +o;
  //             }
  //           }) as SpellLevel[];
  //         }
  //         if (this.extra_base_limits.schools) {
  //           limit.schools = this.extra_base_limits.schools;
  //         }
  //         if (this.extra_base_limits.onSpellList) {
  //           limit.list_ids = this.extra_base_limits.onSpellList;
  //         }
  //         this.extra_base_limit.limits.push(limit);
  //       }
  //     }
  //     this.spell_dc = obj.spell_dc ? new UpgradableNumber(obj.spell_dc) : new UpgradableNumber();
  //     this.spell_attack = obj.spell_attack ? new UpgradableNumber(obj.spell_attack) : new UpgradableNumber();
  //     this.spell_mod = obj.spell_mod ? new UpgradableNumber(obj.spell_mod) : new UpgradableNumber();
  //     // this.level = obj.level ? obj.level : 0;
  //     this.environments = obj.environments ? [...obj.environments] : ["Any"];
  //     // this.settings = obj.settings ? [...obj.settings] : ["Any"];
  //     if (obj.settings && obj.settings.length > 0 && this.world_settings.length === 0) {
  //       this.world_settings = obj.settings as WorldSetting[];
  //     }
  //     if (this.world_settings.includes("Any")) {
  //       this.world_settings = this.world_settings.filter(o => o !== "Any");
  //     }

  //     this.proficiency_req_options = obj.proficiency_req_options ? obj.proficiency_req_options : [];
  //     if (this.proficiency_req_options.includes("True None")) {
  //       this.proficiency_req_options = this.proficiency_req_options.filter(o => o !== "True None");
  //       this.proficiency_req_options.push("None");
  //     }
  //     this.weapon_mastery_override = obj.weapon_mastery_override ? obj.weapon_mastery_override : "";
  //     this.ignore_stealth_disadvantage = obj.ignore_stealth_disadvantage ? obj.ignore_stealth_disadvantage : false;
  //     this.override_weapon_damage_type = obj.override_weapon_damage_type ? obj.override_weapon_damage_type : "";
  //     this.proficiency_as_weapons = obj.proficiency_as_weapons ? obj.proficiency_as_weapons : [];

  //     if (this.name.includes(" Of ")) {
  //       this.name = this.name.replace(" Of ", " of ");
  //     }
  //     if (this.name_formula.includes("{obj.base_name}")) {
  //       this.name_formula = this.name_formula.replace("{obj.base_name}","{base_item.name}");
  //     }
  //     this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
  //     if (this.base_item_types.includes("Weapon") && !obj.weapon_limit) {
  //       this.weapon_limit.weapon_keyword_ids = this.base_item_details;
  //       this.base_item_details = [];
  //     }
  //   }
  // }

  get rarity_string(): string {
    if (this.min_rarity === this.max_rarity) {
      return RARITY_NUM_TO_STR_MAP[this.min_rarity];
    } else {
      return `${RARITY_NUM_TO_STR_MAP[this.min_rarity]} - ${RARITY_NUM_TO_STR_MAP[this.max_rarity]}`;
    }
  }

  get_detail_format() {
    const str = `${super.get_detail_format()}; ${this.rarity_string} - ${this.base_item_types_or_specific}`;
    return str.trim();
  }

  // get color(): string {
  //   let color = "inherit"; // Common
  //   switch (this.rarity) {
  //     case 1: color = "green"; break; // Uncommon
  //     case 2: color = "blue"; break; // Rare
  //     case 3: color = "purple"; break; // Very-Rare
  //     case 4: color = "orange"; break; // Legendary
  //     case 5: color = "brown"; break; // Artifact
  //   }
  //   return color;
  // }

  get base_item_types_or_specific(): string[] {
    if (this.uses_specific) {
      if (this.base_item_ids.length > 0) {
        return [`{list_of_type:item;[${this.base_item_ids}]}`];
      } else {
        return [];
      }
    } else {
      return this.base_item_types;
    }
  }

  toDBObj(): any {
    const feature_source_obj = super.toDBObj();
    // const limitations: any[] = [];
    // this.limitations.forEach(l => {
    //   limitations.push(l.toDBObj());
    // });
    // const bonus_damages: any[] = [];
    // this.bonus_damages.forEach(damage => {
    //   bonus_damages.push(damage.toDBObj());
    // });
    return {
      ...feature_source_obj,
      uses_specific: this.uses_specific,
      base_item_types: this.base_item_types,
      base_item_details: this.base_item_details,
      base_item_ids: this.base_item_ids,
      exclusions: this.exclusions,
      weapon_limit: this.weapon_limit.toDBObj(),
      // rarity: this.rarity,
      min_rarity: this.min_rarity,
      max_rarity: this.max_rarity,
      name_formula: this.name_formula,
      rarity_names: this.rarity_names,
      recipes: this.recipes.map(o => o.toDBObj()),
      attunement: this.attunement,
      limitations: this.limitations.map(o => o.toDBObj()),
      charges: this.charges,
      charge_refresh_rule: this.charge_refresh_rule,
      refresh_dice_count: this.refresh_dice_count,
      refresh_dice_size: this.refresh_dice_size,
      refresh_dice_flat: this.refresh_dice_flat,
      // features,
      // bonus_damages,
      extra_base_type: this.extra_base_type,
      extra_base_limits: this.extra_base_limits,
      extra_base_limit: this.extra_base_limit.toDBObj(),
      spell_dc: this.spell_dc.toDBObj(),
      spell_attack: this.spell_attack.toDBObj(),
      spell_mod: this.spell_mod.toDBObj(),
      // level: this.level
      environments: [...this.environments],
      // settings: [...this.settings],
      proficiency_req_options: this.proficiency_req_options,
      weapon_mastery_override: this.weapon_mastery_override,
      ignore_stealth_disadvantage: this.ignore_stealth_disadvantage,
      override_weapon_damage_type: this.override_weapon_damage_type,
      proficiency_as_weapons: this.proficiency_as_weapons
    };
  }

  toLight(): any {
    const feature_source_obj = super.toLight();
    return {
      ...feature_source_obj,
      uses_specific: this.uses_specific,
      base_item_types: this.base_item_types,
      base_item_details: this.base_item_details,
      base_item_ids: this.base_item_ids,
      exclusions: this.exclusions,
      weapon_limit: this.weapon_limit.toDBObj(),
      min_rarity: this.min_rarity,
      max_rarity: this.max_rarity,
      name_formula: this.name_formula,
      rarity_names: this.rarity_names,
      recipes: this.recipes.map(o => o.toDBObj()),
      attunement: this.attunement,
      limitations: this.limitations.map(o => o.toDBObj()),
      charges: this.charges,
      charge_refresh_rule: this.charge_refresh_rule,
      refresh_dice_count: this.refresh_dice_count,
      refresh_dice_size: this.refresh_dice_size,
      refresh_dice_flat: this.refresh_dice_flat,
      invert_charges: this.invert_charges,
      extra_base_type: this.extra_base_type,
      extra_base_limits: this.extra_base_limits,
      extra_base_limit: this.extra_base_limit.toDBObj(),
      spell_dc: this.spell_dc.toDBObj(),
      spell_attack: this.spell_attack.toDBObj(),
      spell_mod: this.spell_mod.toDBObj(),
      environments: [...this.environments],
      proficiency_req_options: this.proficiency_req_options,
      weapon_mastery_override: this.weapon_mastery_override,
      ignore_stealth_disadvantage: this.ignore_stealth_disadvantage,
      override_weapon_damage_type: this.override_weapon_damage_type,
      proficiency_as_weapons: this.proficiency_as_weapons
    };
  }

  copy(copyMe: MagicItemKeyword, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.uses_specific = copyMe.uses_specific;
    this.base_item_types = [...copyMe.base_item_types];
    this.base_item_details = [...copyMe.base_item_details];
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.base_item_ids = copyMe.base_item_ids;
    this.exclusions = copyMe.exclusions;
    // this.rarity = copyMe.rarity;
    this.min_rarity = copyMe.min_rarity;
    this.max_rarity = copyMe.max_rarity;
    this.name_formula = copyMe.name_formula;
    this.rarity_names = {...copyMe.rarity_names};
    this.recipes = copyMe.recipes.map(o => o.clone(drop_ids));
    this.attunement = copyMe.attunement;
    this.limitations = copyMe.limitations.map(o => o.clone(drop_ids));
    this.charges = copyMe.charges;
    this.charge_refresh_rule = copyMe.charge_refresh_rule;
    this.refresh_dice_count = copyMe.refresh_dice_count;
    this.refresh_dice_size = copyMe.refresh_dice_size;
    this.refresh_dice_flat = copyMe.refresh_dice_flat;
    this.invert_charges = copyMe.invert_charges;
    // this.features = [...copyMe.features];
    // this.bonus_damages = [];
    // copyMe.bonus_damages.forEach(bd => {
    //   this.bonus_damages.push(bd.clone(drop_ids));
    // });
    this.extra_base_type = copyMe.extra_base_type;
    if (this.extra_base_type === "Spell") {
      // const extra_base_limits = copyMe.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] };
      // this.extra_base_limits = {
      //   levels: [...extra_base_limits.levels],
      //   schools: [...extra_base_limits.schools],
      //   onSpellList: [...extra_base_limits.onSpellList]
      // };
    } else if (this.extra_base_type !== "None") {
      const extra_base_limits = copyMe.extra_base_limits as string[];
      this.extra_base_limits = [...extra_base_limits];
    } else {
      this.extra_base_limits = null;
    }
    this.extra_base_limit = copyMe.extra_base_limit.clone();
    this.spell_dc = copyMe.spell_dc.clone();
    this.spell_attack = copyMe.spell_attack.clone();
    this.spell_mod = copyMe.spell_mod.clone();
    this.environments = [...copyMe.environments];
    // this.settings = [...copyMe.settings];
    // this.level = copyMe.level;
    this.proficiency_req_options = [...copyMe.proficiency_req_options];
    this.weapon_mastery_override = copyMe.weapon_mastery_override;
    this.ignore_stealth_disadvantage = copyMe.ignore_stealth_disadvantage;
    this.override_weapon_damage_type = copyMe.override_weapon_damage_type;
    this.proficiency_as_weapons = [...copyMe.proficiency_as_weapons];
  }

  clone(drop_ids = false): MagicItemKeyword {
    const new_obj = new MagicItemKeyword();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}